// ═══════════════════════════════════════════════════════════
// Staff Login — split layout
// ═══════════════════════════════════════════════════════════

import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconLock, IconMail } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const { login, loading, error } = useAuthStore();
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    try {
      await login(email, password);
      navigate('/staff');
    } catch { /* error shown via store */ }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--color-neutral-100)',
      padding: 32,
    }}>
      <div style={{
        display: 'flex',
        width: '100%',
        maxWidth: 1482,
        minHeight: 897,
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 8px 40px rgba(0,0,0,0.14)',
        background: 'white',
      }}>

      {/* ── Left panel: building photo ── */}
      <div style={{
        flex: '0 0 45%',
        position: 'relative',
        display: 'flex',
      }}>
        <img
          src="/building.jpg"
          alt="Government building"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
        {/* Gradient overlay + title at bottom */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '80px 28px 28px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.72) 0%, transparent 100%)',
        }}>
          <p style={{
            margin: 0,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.65)',
            marginBottom: 6,
          }}>
            Your Organization
          </p>
          <h1 style={{
            margin: 0,
            fontSize: 36,
            fontWeight: 700,
            color: 'white',
            lineHeight: 1.15,
          }}>
            Open Records
          </h1>
        </div>
      </div>

      {/* ── Right panel: login form ── */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 48px',
        background: 'white',
      }}>
        {/* OpenGov logo + wordmark */}
        <div style={{ marginBottom: 40, textAlign: 'center' }}>
          <img
            src="/opengov-mark.svg"
            alt="OpenGov"
            style={{ height: 36, marginBottom: 10 }}
          />
          <p style={{
            margin: 0,
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--color-neutral-400)',
            letterSpacing: '0.04em',
          }}>
            OpenRecords Staff Portal
          </p>
        </div>

        {/* Form card */}
        <div style={{ width: '100%', maxWidth: 380 }}>
          <h2 style={{
            margin: '0 0 6px',
            fontSize: 22,
            fontWeight: 700,
            color: 'var(--color-neutral-900)',
          }}>
            Sign in
          </h2>
          <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--color-neutral-500)' }}>
            Enter your credentials to access the staff portal.
          </p>

          <form onSubmit={onSubmit}>
            <div style={{
              background: 'white',
              border: '1px solid var(--color-neutral-200)',
              borderRadius: 12,
              padding: 24,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              {error && (
                <div style={{
                  background: 'var(--color-error-bg)',
                  border: '1px solid #fca5a5',
                  borderRadius: 6,
                  padding: '10px 12px',
                  marginBottom: 16,
                  fontSize: 13,
                  color: 'var(--color-error)',
                }}>
                  {error}
                </div>
              )}

              <div className="form-field">
                <label className="form-label required">Email address</label>
                <div style={{ position: 'relative' }}>
                  <IconMail size={15} style={{
                    position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--color-neutral-400)',
                  }} />
                  <input
                    type="email"
                    className="form-input"
                    style={{ paddingLeft: 32, width: '100%' }}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                    placeholder="you@agency.gov"
                  />
                </div>
              </div>

              <div className="form-field" style={{ marginBottom: 20 }}>
                <label className="form-label required">Password</label>
                <div style={{ position: 'relative' }}>
                  <IconLock size={15} style={{
                    position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--color-neutral-400)',
                  }} />
                  <input
                    type="password"
                    className="form-input"
                    style={{ paddingLeft: 32, width: '100%' }}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center', padding: '10px' }}
                disabled={loading}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>
            </div>
          </form>

          <p style={{ textAlign: 'center', marginTop: 20, fontSize: 12, color: 'var(--color-neutral-400)' }}>
            Public records requests?{' '}
            <a href="/portal" style={{ color: 'var(--color-primary-700)' }}>Visit the public portal →</a>
          </p>
        </div>
      </div>

      </div> {/* card */}
    </div>
  );
}
